import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Statistic, Spin, Select, Tag, Alert } from "antd";
import {
  EyeOutlined,
  WarningOutlined,
  LogoutOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const REFRESH_INTERVAL = 10000; // 10 seconds like Mux dashboard

const MonitoringSection = ({
  muxError,
  concurrentViewers,
  startupHealth,
  tracks,
  loading,
  fetchConcurrentViewers,
  fetchStartupHealth,
}) => {
  const [selectedTrailId, setSelectedTrailId] = useState(undefined);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  const loadData = (trailId) => {
    fetchConcurrentViewers(trailId);
    fetchStartupHealth();
    setLastUpdated(new Date());
  };

  useEffect(() => {
    loadData(selectedTrailId);
    intervalRef.current = setInterval(() => {
      loadData(selectedTrailId);
    }, REFRESH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrailId]);

  // Tick "updated Xs ago" every second
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (lastUpdated) {
        setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [lastUpdated]);

  const handleTrailChange = (trailId) => {
    setSelectedTrailId(trailId || undefined);
  };

  const trackList = tracks?.data || tracks || [];
  const currentViewers = concurrentViewers?.currentViewers || 0;
  const viewerData = concurrentViewers?.data || [];

  return (
    <div>
      {muxError && (
        <Alert
          message="Mux Data API"
          description={muxError}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Header with trail filter */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <Select
          placeholder="Filtrar por trilha (opcional)"
          style={{ width: 400 }}
          allowClear
          showSearch
          optionFilterProp="children"
          value={selectedTrailId}
          onChange={handleTrailChange}
        >
          {trackList.map((t) => (
            <Option key={t._id || t.id} value={t._id || t.id}>{t.name || t.title}</Option>
          ))}
        </Select>
        <Tag icon={<SyncOutlined spin />} color="processing">
          Auto-refresh {REFRESH_INTERVAL / 1000}s
        </Tag>
      </div>

      {/* Real-time Viewers Widget */}
      <Card
        bordered={false}
        style={{ marginBottom: 24, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
        bodyStyle={{ padding: "24px 32px" }}
      >
        <Row align="middle" gutter={32}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 4 }}>
                <EyeOutlined style={{ marginRight: 6 }} />
                Real-time views
              </div>
              {loading.monitoring && !concurrentViewers ? (
                <Spin size="large" />
              ) : (
                <div style={{ color: "#fff", fontSize: 64, fontWeight: 700, lineHeight: 1.1 }}>
                  {currentViewers}
                </div>
              )}
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>
                current views
              </div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, marginTop: 2 }}>
                Updated {secondsAgo}s ago
              </div>
            </div>
          </Col>
          <Col xs={24} md={16}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginBottom: 8 }}>
              Real-time views last 30 minutes
            </div>
            <RealtimeChart data={viewerData} />
          </Col>
        </Row>
      </Card>

      {/* Startup Health Cards */}
      <div style={{ marginBottom: 24 }}>
        <StartupHealthCards data={startupHealth} loading={loading.monitoring} />
      </div>
    </div>
  );
};

// Mini bar chart for real-time viewers (CSS-only, no chart library needed)
const RealtimeChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>Sem dados</span>
      </div>
    );
  }

  // Take last 30 data points (approx 30 minutes if data is per-minute)
  const recentData = data.slice(-30);
  const values = recentData.map((d) => d.concurrent_viewers || d.value || 0);
  const maxVal = Math.max(...values, 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 80 }}>
      {values.map((val, idx) => {
        const height = Math.max((val / maxVal) * 100, 2);
        const isLatest = idx === values.length - 1;
        return (
          <div
            key={idx}
            title={`${recentData[idx]?.date ? new Date(recentData[idx].date).toLocaleTimeString("pt-BR") : ""}: ${val} viewers`}
            style={{
              flex: 1,
              height: `${height}%`,
              backgroundColor: isLatest ? "#fff" : "rgba(255,255,255,0.4)",
              borderRadius: "2px 2px 0 0",
              transition: "height 0.3s ease",
              cursor: "pointer",
              minWidth: 3,
            }}
          />
        );
      })}
    </div>
  );
};

const StartupHealthCards = ({ data, loading }) => {
  if (loading && !data) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) return null;

  const cards = [
    {
      title: "Playback Failure Rate",
      value: data.playbackFailureRate?.toFixed(2) || 0,
      suffix: "%",
      icon: <CloseCircleOutlined />,
      color: "#ff4d4f",
      status: (data.playbackFailureRate || 0) > 1 ? "danger" : "ok",
    },
    {
      title: "Exits Before Start",
      value: data.exitsBeforeVideoStart?.toFixed(2) || 0,
      suffix: "%",
      icon: <LogoutOutlined />,
      color: "#fa8c16",
      status: (data.exitsBeforeVideoStart || 0) > 2 ? "danger" : "ok",
    },
    {
      title: "Startup Failure Rate",
      value: data.startupFailureRate?.toFixed(2) || 0,
      suffix: "%",
      icon: <WarningOutlined />,
      color: "#eb2f96",
      status: (data.startupFailureRate || 0) > 1 ? "danger" : "ok",
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {cards.map((card) => (
        <Col xs={24} sm={8} key={card.title}>
          <Card
            bordered={false}
            style={{
              borderTop: `3px solid ${card.color}`,
              borderLeft: card.status === "danger" ? `4px solid ${card.color}` : undefined,
            }}
          >
            <Statistic
              title={
                <span>
                  {React.cloneElement(card.icon, {
                    style: { color: card.color, marginRight: 8 },
                  })}
                  {card.title}
                </span>
              }
              value={card.value}
              suffix={<span style={{ fontSize: 14 }}>{card.suffix}</span>}
              valueStyle={{
                fontSize: 28,
                color: card.status === "danger" ? card.color : undefined,
              }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default MonitoringSection;
